import React, { useState, useEffect } from 'react';
import { Task } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { generateBreadcrumbPath } from '../components/common/breadcrumbs';
import { toast } from 'react-toastify';
import {
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Typography,
} from '@mui/material';
import { CheckCircle, Cancel, PlayArrow } from '@mui/icons-material';
import { withBrowserTimezoneHeaders } from '../utils/browserTimezone';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/agent/static-tasks`;

// Feature flag: Control which trigger routes are available
// Set to true to enable all trigger routes, false to only allow LLM
const ENABLE_ALL_TRIGGER_ROUTES = false;

const DAY_OF_WEEK_LABELS = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
const MONTH_OF_YEAR_LABELS = {
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

const parseCsvToNumberArray = (csv) => {
  if (!csv || !String(csv).trim()) return [];
  return String(csv).split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
};

const toCsv = (arr) => {
  if (!arr || !arr.length) return null;
  return [...arr].sort((a, b) => a - b).join(',');
};

const WeekdayChecklistField = ({ value, onChange, error }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (day) => {
    const next = selected.includes(day)
      ? selected.filter((d) => d !== day)
      : [...selected, day].sort((a, b) => a - b);
    onChange(next);
  };
  return (
    <Box>
      <FormHelperText sx={{ mb: 0.5 }}>Days of week (select one or more)</FormHelperText>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
          <FormControlLabel
            key={day}
            control={
              <Checkbox
                size="small"
                checked={selected.includes(day)}
                onChange={() => toggle(day)}
              />
            }
            label={DAY_OF_WEEK_LABELS[day]}
          />
        ))}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

const MonthChecklistField = ({ value, onChange, error }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (month) => {
    const next = selected.includes(month)
      ? selected.filter((m) => m !== month)
      : [...selected, month].sort((a, b) => a - b);
    onChange(next);
  };
  return (
    <Box>
      <FormHelperText sx={{ mb: 0.5 }}>Months (select one or more)</FormHelperText>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
          <FormControlLabel
            key={month}
            control={
              <Checkbox
                size="small"
                checked={selected.includes(month)}
                onChange={() => toggle(month)}
              />
            }
            label={MONTH_OF_YEAR_LABELS[month]}
          />
        ))}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

const buildTimeBasedScheduleFields = (data) => {
  if (data.runType !== 'TIME_BASED') {
    return {
      recurrence: null,
      dayOfWeek: null,
      daysOfWeek: null,
      dayOfMonth: null,
      monthsOfYear: null,
      scheduleStartDate: null,
      scheduleEndDate: null,
    };
  }
  const recurrence = data.recurrence || 'DAILY';
  const weekdays = Array.isArray(data.selectedWeekdays) ? data.selectedWeekdays : [];
  const months = Array.isArray(data.selectedMonths) ? data.selectedMonths : [];
  return {
    recurrence,
    dayOfWeek: null,
    daysOfWeek: recurrence === 'WEEKLY' ? toCsv(weekdays) : null,
    dayOfMonth: recurrence === 'MONTHLY' && data.dayOfMonth != null && data.dayOfMonth !== ''
      ? parseInt(data.dayOfMonth, 10) : null,
    monthsOfYear: recurrence === 'MONTHLY' ? toCsv(months) : null,
    scheduleStartDate: data.scheduleStartDate && String(data.scheduleStartDate).trim()
      ? String(data.scheduleStartDate).trim() : null,
    scheduleEndDate: recurrence === 'ONE_TIME' ? null
      : (data.scheduleEndDate && String(data.scheduleEndDate).trim()
        ? String(data.scheduleEndDate).trim() : null),
  };
};

const formatTimeBasedScheduleLabel = (row) => {
  const time = row.runTime || '';
  const recurrence = row.recurrence || 'DAILY';
  if (recurrence === 'ONE_TIME') {
    const date = row.scheduleStartDate ? String(row.scheduleStartDate).substring(0, 10) : '';
    return `Once ${date} ${time}`.trim();
  }
  if (recurrence === 'WEEKLY') {
    const days = parseCsvToNumberArray(row.daysOfWeek);
    const dayLabels = days.length
      ? days.map((d) => DAY_OF_WEEK_LABELS[d] || d).join(', ')
      : (DAY_OF_WEEK_LABELS[row.dayOfWeek] || '');
    return `Weekly ${dayLabels} ${time}`.trim();
  }
  if (recurrence === 'MONTHLY') {
    const months = parseCsvToNumberArray(row.monthsOfYear);
    const monthLabels = months.length
      ? months.map((m) => MONTH_OF_YEAR_LABELS[m] || m).join(', ')
      : '';
    const monthPart = monthLabels ? `(${monthLabels}) ` : '';
    return `Monthly ${monthPart}day ${row.dayOfMonth || ''} ${time}`.trim();
  }
  return time || 'Daily';
};

const token = localStorage.getItem('token');
const tenantId = localStorage.getItem('tenantId');

const AgentStaticTasks = ({ agentName: propAgentName, agentDisplayName: propAgentDisplayName, drawerMode = false }) => {
  const [staticTasks, setStaticTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  /** Full tenant agents: { agentName, agentUniqueId } for dropdown (value=uniqueId) and grid display (id -> name) */
  const [tenantAgents, setTenantAgents] = useState([]);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deactivateTask, setDeactivateTask] = useState(null);
  const [runningTaskIds, setRunningTaskIds] = useState(() => new Set());

  const buildEventPayloadTemplate = (data) => {
    if (data.runType !== 'EVENT_BASED') {
      return null;
    }
    const promptText = (data.questions && data.questions.trim())
      || (data.eventPrompt && data.eventPrompt.trim())
      || '';
    const payload = {
      event_prompt: promptText,
      inbound_source: 'WEBHOOK',
    };
    let connectorId = data.inboundConnectorId && String(data.inboundConnectorId).trim();
    if (!connectorId && data.payloadTemplate) {
      try {
        const existing = typeof data.payloadTemplate === 'string'
          ? JSON.parse(data.payloadTemplate)
          : data.payloadTemplate;
        if (existing && existing.inbound_connector_id) {
          connectorId = String(existing.inbound_connector_id).trim();
        }
      } catch (_) {
        /* ignore */
      }
    }
    if (connectorId) {
      payload.inbound_connector_id = connectorId;
    }
    return JSON.stringify(payload);
  };

  useEffect(() => {
    fetchStaticTasks();
    fetchTenantAgents();
  }, []);

  // When in drawer mode, re-fetch tasks for the selected agent when agentName changes
  useEffect(() => {
    if (drawerMode && propAgentName) {
      fetchStaticTasks();
    }
  }, [drawerMode, propAgentName]);

  /** Map stored agent id (tenant_agent_instance_id or legacy agentUniqueId/agentName) -> display name for grid */
  const agentIdToDisplayName = React.useMemo(() => {
    const map = {};
    tenantAgents.forEach((a) => {
      const instanceId = a.tenantAgentInstanceId ?? a.agentUniqueId ?? a.agentName;
      if (instanceId && a.agentName) map[instanceId] = a.agentName;
      if (a.agentName) map[a.agentName] = a.agentName;
    });
    return map;
  }, [tenantAgents]);

  const fetchTenantAgents = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}asrosync/agents/tenant/${tenantId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const agents = await response.json();
        setTenantAgents(agents.filter((a) => a.agentName || a.tenantAgentInstanceId || a.agentUniqueId));
      }
    } catch (error) {
      console.error('Error fetching tenant agents:', error);
    }
  };

  const fetchStaticTasks = async () => {
    setIsLoading(true);
    try {
      // In drawer mode with a specific agent, fetch only that agent's tasks so they aren't shared across rows
      const url = drawerMode && propAgentName
        ? `${API_BASE_URL}/tenant/${tenantId}/agent/${encodeURIComponent(propAgentName)}`
        : `${API_BASE_URL}/tenant/${tenantId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const mappedData = data.map((item) => ({
        id: item.staticTaskId,
        staticTaskId: item.staticTaskId,
        agentName: item.agentName,
        taskName: item.taskName,
        taskType: item.taskType,
        runType: item.runType,
        runTime: item.runTime,
        runIntervalMin: item.runIntervalMin,
        isActive: item.isActive,
        priority: item.priority,
        activeFrom: item.activeFrom,
        activeTo: item.activeTo,
        recurrence: item.recurrence || 'DAILY',
        dayOfWeek: item.dayOfWeek,
        daysOfWeek: item.daysOfWeek || '',
        dayOfMonth: item.dayOfMonth,
        monthsOfYear: item.monthsOfYear || '',
        scheduleStartDate: item.scheduleStartDate || '',
        scheduleEndDate: item.scheduleEndDate || '',
        notifyOnSeverity: item.notifyOnSeverity,
        runActions: item.runActions,
        payloadTemplate: item.payloadTemplate,
      }));

      setStaticTasks(mappedData);
    } catch (error) {
      console.error('Error fetching static tasks:', error);
      toast.error(`Error loading static tasks: ${error.message}`);
      setStaticTasks([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      if (!data.triggerRoute || !String(data.triggerRoute).trim()) {
        toast.error('Trigger Route is required. Tasks without it fail in the queue.');
        throw new Error('Trigger Route is required');
      }
      // Parse runActions and merge trigger_route + questions
      let runActionsValue = null;
      try {
        let parsed = (data.runActions && data.runActions.trim())
          ? (typeof data.runActions === 'string' ? JSON.parse(data.runActions) : data.runActions)
          : { mode: 'CHECK' };
        if (!parsed || typeof parsed !== 'object') parsed = { mode: 'CHECK' };
        const route = data.triggerRoute;
        if (route) parsed.trigger_route = route;
        if (data.questions && data.questions.trim()) {
          parsed.questions = data.questions.trim();
        }
        if (route === 'LLM') {
          parsed.decisioning = parsed.decisioning || {};
          parsed.decisioning.use_llm = true;
        }
        runActionsValue = JSON.stringify(parsed);
      } catch (e) {
        throw new Error('Invalid JSON in Run Actions: ' + e.message);
      }

      let payloadTemplateValue = buildEventPayloadTemplate(data);
      if (!payloadTemplateValue && data.payloadTemplate && data.payloadTemplate.trim()) {
        try {
          const parsed = typeof data.payloadTemplate === 'string'
            ? JSON.parse(data.payloadTemplate)
            : data.payloadTemplate;
          payloadTemplateValue = JSON.stringify(parsed);
        } catch (e) {
          throw new Error('Invalid JSON in Payload Template: ' + e.message);
        }
      }

      // Helper to ensure time format is HH:mm:ss
      const formatTime = (timeStr) => {
        if (!timeStr) return null;
        // If already HH:mm:ss format, return as-is
        if (timeStr.length === 8 && timeStr.includes(':')) {
          return timeStr;
        }
        // If HH:mm format, add :00
        if (timeStr.length === 5 && timeStr.match(/^\d{2}:\d{2}$/)) {
          return timeStr + ':00';
        }
        return timeStr;
      };

      // In drawer mode, always use the drawer's agent (disabled fields may be omitted from submit)
      const effectiveAgentName = (drawerMode && propAgentName) ? propAgentName : (data.agentName || '');
      const cleanData = {
        agentName: effectiveAgentName,
        taskName: data.taskName,
        taskType: data.taskType,
        runType: data.runType,
        runTime: data.runType === 'TIME_BASED' ? formatTime(data.runTime) : null,
        runIntervalMin: data.runType === 'INTERVAL_BASED' ? (data.runIntervalMin ? parseInt(data.runIntervalMin) : null) : null,
        activeFrom: formatTime(data.activeFrom) || '06:00:00',
        activeTo: formatTime(data.activeTo) || '22:00:00',
        isActive: data.isActive !== undefined ? data.isActive : true,
        priority: data.priority ? parseInt(data.priority) : 50,
        notifyOnSeverity: data.notifyOnSeverity || 'HIGH',
        runActions: runActionsValue,
        payloadTemplate: payloadTemplateValue,
        ...buildTimeBasedScheduleFields(data),
      };

      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cleanData),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }

      await response.json();
      toast.success('Static task created successfully');
      await fetchStaticTasks();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(`Error creating static task: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      if (!data.triggerRoute || !String(data.triggerRoute).trim()) {
        toast.error('Trigger Route is required. Tasks without it fail in the queue.');
        throw new Error('Trigger Route is required');
      }
      // Parse runActions and merge trigger_route + questions from form
      let runActionsValue = null;
      try {
        let parsed = (data.runActions && data.runActions.trim())
          ? (typeof data.runActions === 'string' ? JSON.parse(data.runActions) : data.runActions)
          : { mode: 'CHECK' };
        if (!parsed || typeof parsed !== 'object') parsed = { mode: 'CHECK' };
        const route = data.triggerRoute;
        if (route) parsed.trigger_route = route;
        if (data.questions && data.questions.trim()) {
          parsed.questions = data.questions.trim();
        }
        if (route === 'LLM') {
          parsed.decisioning = parsed.decisioning || {};
          parsed.decisioning.use_llm = true;
        }
        runActionsValue = JSON.stringify(parsed);
      } catch (e) {
        throw new Error('Invalid JSON in Run Actions: ' + e.message);
      }

      let payloadTemplateValue = buildEventPayloadTemplate(data);
      if (!payloadTemplateValue && data.payloadTemplate && data.payloadTemplate.trim()) {
        try {
          const parsed = typeof data.payloadTemplate === 'string'
            ? JSON.parse(data.payloadTemplate)
            : data.payloadTemplate;
          payloadTemplateValue = JSON.stringify(parsed);
        } catch (e) {
          throw new Error('Invalid JSON in Payload Template: ' + e.message);
        }
      }

      // Helper to ensure time format is HH:mm:ss
      const formatTime = (timeStr) => {
        if (!timeStr) return null;
        // If already HH:mm:ss format, return as-is
        if (timeStr.length === 8 && timeStr.includes(':')) {
          return timeStr;
        }
        // If HH:mm format, add :00
        if (timeStr.length === 5 && timeStr.match(/^\d{2}:\d{2}$/)) {
          return timeStr + ':00';
        }
        return timeStr;
      };

      const cleanData = {
        tenantId: Number(tenantId),
        taskName: data.taskName,
        taskType: data.taskType,
        runType: data.runType,
        runTime: data.runType === 'TIME_BASED' ? formatTime(data.runTime) : null,
        runIntervalMin: data.runType === 'INTERVAL_BASED' ? (data.runIntervalMin ? parseInt(data.runIntervalMin) : null) : null,
        activeFrom: formatTime(data.activeFrom) || '06:00:00',
        activeTo: formatTime(data.activeTo) || '22:00:00',
        isActive: data.isActive !== undefined ? data.isActive : true,
        priority: data.priority ? parseInt(data.priority) : 50,
        notifyOnSeverity: data.notifyOnSeverity || 'HIGH',
        runActions: runActionsValue,
        payloadTemplate: payloadTemplateValue,
        ...buildTimeBasedScheduleFields(data),
      };

      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cleanData),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }

      await response.json();
      toast.success('Static task updated successfully');
      await fetchStaticTasks();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(`Error updating static task: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('Static task deleted successfully');
      await fetchStaticTasks();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Error deleting static task: ${error.message}`);
      throw error;
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${id}/active?isActive=${!currentStatus}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success(`Static task ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchStaticTasks();
    } catch (error) {
      console.error('Toggle active error:', error);
      toast.error(`Error updating task status: ${error.message}`);
    }
  };

  const openDeactivateConfirm = (id, taskName) => {
    setDeactivateTask({ id, taskName });
    setDeactivateConfirmOpen(true);
  };

  const closeDeactivateConfirm = () => {
    setDeactivateConfirmOpen(false);
    setDeactivateTask(null);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTask) return;
    await handleToggleActive(deactivateTask.id, true);
    closeDeactivateConfirm();
  };

  const handleRun = async (staticTaskId, taskName) => {
    setRunningTaskIds((prev) => new Set([...prev, staticTaskId]));
    try {
      const response = await fetch(`${API_BASE_URL}/${staticTaskId}/run?tenantId=${tenantId}`, {
        method: 'POST',
        headers: withBrowserTimezoneHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        }),
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = body.message || `HTTP error! status: ${response.status}`;
        if (body.error === 'RUN_BLOCKED') {
          toast.warning(msg);
        } else {
          toast.error(`Run failed: ${msg}`);
        }
        return;
      }

      const finalStatus = body.finalStatus || 'UNKNOWN';
      if (body.success) {
        toast.success(`"${taskName}" completed (${finalStatus}). Check Task Queue for details.`);
      } else {
        toast.error(`"${taskName}" finished with status: ${finalStatus}. Check Task Queue for details.`);
      }
    } catch (error) {
      console.error('Manual run error:', error);
      toast.error(`Error running task: ${error.message}`);
    } finally {
      setRunningTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(staticTaskId);
        return next;
      });
    }
  };

  const prepareEditData = async (row) => {
    let triggerRoute = '';
    let questions = '';
    if (row.runActions) {
      try {
        const ra = typeof row.runActions === 'string' ? JSON.parse(row.runActions) : row.runActions;
        if (ra && typeof ra === 'object') {
          if (ra.trigger_route) triggerRoute = ra.trigger_route;
          questions = ra.questions || ra.prompt || '';
        }
      } catch (_) { }
    }
    const af = (row.activeFrom || '').toString().trim();
    const at = (row.activeTo || '').toString().trim();
    const is24_7 = (af === '00:00' || af === '00:00:00') && (at === '23:59' || at === '23:59:00' || at === '23:59:59');
    // Normalize time to HH:mm for time inputs (API may return HH:mm:ss; input type="time" uses HH:mm)
    const toHHmm = (t) => {
      if (!t) return '';
      const s = String(t).trim();
      const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
      return m ? `${m[1].padStart(2, '0')}:${m[2]}` : s;
    };
    const base = {
      agentName: row.agentName,
      taskName: row.taskName,
      taskType: row.taskType,
      runType: row.runType,
      runTime: toHHmm(row.runTime) || '',
      runIntervalMin: row.runIntervalMin || '',
      recurrence: row.recurrence || 'DAILY',
      selectedWeekdays: (() => {
        const fromCsv = parseCsvToNumberArray(row.daysOfWeek);
        if (fromCsv.length) return fromCsv;
        return row.dayOfWeek != null ? [row.dayOfWeek] : [1];
      })(),
      dayOfMonth: row.dayOfMonth != null ? row.dayOfMonth : 1,
      selectedMonths: (() => {
        const fromCsv = parseCsvToNumberArray(row.monthsOfYear);
        return fromCsv.length ? fromCsv : [1];
      })(),
      scheduleStartDate: row.scheduleStartDate ? String(row.scheduleStartDate).substring(0, 10) : '',
      scheduleEndDate: row.scheduleEndDate ? String(row.scheduleEndDate).substring(0, 10) : '',
      activeWindowPreset: is24_7 ? '24_7' : 'custom',
      activeFrom: toHHmm(row.activeFrom) || '06:00',
      activeTo: toHHmm(row.activeTo) || '22:00',
      isActive: row.isActive !== undefined ? row.isActive : true,
      priority: row.priority || 50,
      notifyOnSeverity: row.notifyOnSeverity || 'HIGH',
      triggerRoute,
      questions,
      runActions: row.runActions ? JSON.stringify(row.runActions, null, 2) : '',
      payloadTemplate: row.payloadTemplate ? JSON.stringify(row.payloadTemplate, null, 2) : '',
      staticTaskId: row.staticTaskId || row.id || '',
    };
    if (row.runType === 'EVENT_BASED' && !base.questions) {
      try {
        const pt = row.payloadTemplate;
        if (pt) {
          const o = typeof pt === 'string' ? JSON.parse(pt) : pt;
          if (o && o.event_prompt) {
            base.questions = String(o.event_prompt);
          }
          if (o && o.inbound_connector_id) {
            base.inboundConnectorId = String(o.inbound_connector_id);
          }
        }
      } catch (_) { /* ignore */ }
    }
    return base;
  };

  const columns = [
    { field: 'taskName', headerName: 'Task Name', width: 200 },
    {
      field: 'agentName',
      headerName: 'Agent Name',
      width: 150,
      renderCell: (params) => agentIdToDisplayName[params.value] || params.value,
    },
    {
      field: 'taskType',
      headerName: 'Task Type',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'runType',
      headerName: 'Run Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'schedule',
      headerName: 'Schedule',
      width: 150,
      renderCell: (params) => {
        if (params.row.runType === 'TIME_BASED') {
          return formatTimeBasedScheduleLabel(params.row);
        } else if (params.row.runType === 'INTERVAL_BASED') {
          return `${params.row.runIntervalMin} min`;
        } else if (params.row.runType === 'EVENT_BASED') {
          return 'Event-based';
        }
        return 'N/A';
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircle /> : <Cancel />}
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'run',
      headerName: 'Run',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const isRunning = runningTaskIds.has(params.row.staticTaskId);
        return (
          <Tooltip title="Run now (enqueue and execute once)">
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={isRunning}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRun(params.row.staticTaskId, params.row.taskName);
                }}
              >
                {isRunning ? <CircularProgress size={18} /> : <PlayArrow />}
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.isActive ? 'Deactivate' : 'Activate'}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (params.row.isActive) {
                openDeactivateConfirm(params.row.staticTaskId, params.row.taskName);
              } else {
                handleToggleActive(params.row.staticTaskId, params.row.isActive);
              }
            }}
            color={params.row.isActive ? 'warning' : 'success'}
          >
            {params.row.isActive ? <Cancel /> : <CheckCircle />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const formFields = React.useMemo(() => [
    {
      name: 'agentName',
      label: 'Agent',
      required: true,
      type: 'select',
      width: 6,
      // Value saved is hub tenant_agent_instance_id (so executor agent_id resolves correctly). Fallback: agentUniqueId then agentName.
      // In drawer mode ensure current agent option shows real name (propAgentDisplayName), not unique id
      options: (drawerMode && propAgentName && propAgentDisplayName)
        ? [{ value: propAgentName, label: propAgentDisplayName }, ...tenantAgents.filter((a) => (a.tenantAgentInstanceId ?? a.agentUniqueId ?? a.agentName) !== propAgentName).map((a) => ({ value: a.tenantAgentInstanceId ?? a.agentUniqueId ?? a.agentName, label: a.agentName || a.tenantAgentInstanceId || a.agentUniqueId || 'Unknown' }))]
        : tenantAgents.map((a) => ({ value: a.tenantAgentInstanceId ?? a.agentUniqueId ?? a.agentName, label: a.agentName || a.tenantAgentInstanceId || a.agentUniqueId || 'Unknown' })),
      validate: (value) => (!value ? 'Agent is required' : null),
      disabled: !!propAgentName, // Disable when in drawer (agent fixed to current row)
    },
    {
      name: 'taskName',
      label: 'Task Name',
      required: true,
      type: 'text',
      width: 6,
      validate: (value) => (!value ? 'Task name is required' : null),
    },
    {
      name: 'taskType',
      label: 'Task Type',
      required: true,
      type: 'text',
      width: 6,
      helperText: 'e.g. Calendar Check, Mail Check, Slack Check, Action To Perform',
      validate: (value) => (!value || !value.trim() ? 'Task type is required' : null),
    },
    {
      name: 'runType',
      label: 'Run Type',
      required: true,
      type: 'select',
      width: 6,
      options: [
        { value: 'TIME_BASED', label: 'Time Based' },
        { value: 'INTERVAL_BASED', label: 'Interval Based' },
        { value: 'EVENT_BASED', label: 'Event Based' },
      ],
      validate: (value) => (!value ? 'Run type is required' : null),
    },
    {
      name: 'runTime',
      label: 'Run Time',
      required: (formData) => formData && formData.runType === 'TIME_BASED',
      type: 'text',
      width: 6,
      inputType: 'time',
      show: (formData) => formData && formData.runType === 'TIME_BASED',
      helperText: 'Time when task should run (HH:MM format)',
      validate: (value, formData) => {
        if (formData && formData.runType === 'TIME_BASED' && !value) {
          return 'Run Time is required for Time Based tasks';
        }
        if (!value) return null;
        // Normalize: strip optional seconds and AM/PM (browser or API may send "05:22:00" or "05:22 AM")
        const normalized = String(value).trim().replace(/\s*(AM|PM)$/i, '').replace(/^(\d{1,2}:\d{2})(:\d{2})?$/, '$1');
        if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(normalized)) {
          return 'Invalid time format. Use HH:MM format';
        }
        return null;
      },
    },
    {
      name: 'recurrence',
      label: 'Recurrence',
      type: 'select',
      width: 6,
      show: (formData) => formData && formData.runType === 'TIME_BASED',
      options: [
        { value: 'DAILY', label: 'Daily' },
        { value: 'WEEKLY', label: 'Weekly' },
        { value: 'MONTHLY', label: 'Monthly' },
        { value: 'ONE_TIME', label: 'One Time' },
      ],
    },
    {
      name: 'selectedWeekdays',
      label: 'Days of Week',
      type: 'custom',
      width: 12,
      component: WeekdayChecklistField,
      show: (formData) => formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'WEEKLY',
      validate: (value, formData) => {
        if (formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'WEEKLY') {
          const days = Array.isArray(value) ? value : [];
          if (!days.length) {
            return 'Select at least one day of week';
          }
        }
        return null;
      },
    },
    {
      name: 'dayOfMonth',
      label: 'Day of Month',
      required: (formData) => formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'MONTHLY',
      type: 'text',
      width: 6,
      inputType: 'number',
      show: (formData) => formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'MONTHLY',
      helperText: 'Day of month (1-31)',
      validate: (value, formData) => {
        if (formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'MONTHLY') {
          if (!value) {
            return 'Day of Month is required for Monthly tasks';
          }
          const n = parseInt(value, 10);
          if (isNaN(n) || n < 1 || n > 31) {
            return 'Day of Month must be between 1 and 31';
          }
        }
        return null;
      },
    },
    {
      name: 'selectedMonths',
      label: 'Months',
      type: 'custom',
      width: 12,
      component: MonthChecklistField,
      show: (formData) => formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'MONTHLY',
      validate: (value, formData) => {
        if (formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'MONTHLY') {
          const months = Array.isArray(value) ? value : [];
          if (!months.length) {
            return 'Select at least one month';
          }
        }
        return null;
      },
    },
    {
      name: 'scheduleStartDate',
      label: 'Start Date',
      required: (formData) => formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'ONE_TIME',
      type: 'text',
      width: 6,
      inputType: 'date',
      show: (formData) => formData && formData.runType === 'TIME_BASED',
      helperText: 'Required for One Time (run date). Optional campaign start for Daily, Weekly, or Monthly.',
      validate: (value, formData) => {
        if (formData && formData.runType === 'TIME_BASED' && formData.recurrence === 'ONE_TIME' && !value) {
          return 'Run Date is required for One Time tasks';
        }
        return null;
      },
    },
    {
      name: 'scheduleEndDate',
      label: 'End Date',
      type: 'text',
      width: 6,
      inputType: 'date',
      show: (formData) => formData && formData.runType === 'TIME_BASED' && formData.recurrence !== 'ONE_TIME',
      helperText: 'Optional last calendar day the task may run',
      validate: (value, formData) => {
        if (formData && formData.scheduleStartDate && value && value < formData.scheduleStartDate) {
          return 'End Date must be on or after Start Date';
        }
        return null;
      },
    },
    {
      name: 'runIntervalMin',
      label: 'Run Interval (minutes)',
      required: (formData) => formData && formData.runType === 'INTERVAL_BASED',
      type: 'text',
      width: 6,
      inputType: 'number',
      show: (formData) => formData && formData.runType === 'INTERVAL_BASED',
      helperText: 'Interval in minutes between task runs',
      validate: (value, formData) => {
        if (formData && formData.runType === 'INTERVAL_BASED' && !value) {
          return 'Run Interval is required for Interval Based tasks';
        }
        if (value && (isNaN(parseInt(value)) || parseInt(value) <= 0)) {
          return 'Run interval must be a positive number';
        }
        return null;
      },
    },
    {
      name: 'activeFrom',
      label: 'Active From',
      type: 'text',
      width: 6,
      inputType: 'time',
      helperText: 'Start time of active window (default: 06:00)',
    },
    {
      name: 'activeTo',
      label: 'Active To',
      type: 'text',
      width: 6,
      inputType: 'time',
      helperText: 'End time of active window (default: 22:00)',
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'text',
      width: 6,
      inputType: 'number',
      helperText: 'Task priority (default: 50)',
    },
    {
      name: 'notifyOnSeverity',
      label: 'Notify On Severity',
      type: 'select',
      width: 6,
      options: [
        { value: 'LOW', label: 'Low' },
        { value: 'MEDIUM', label: 'Medium' },
        { value: 'HIGH', label: 'High' },
        { value: 'CRITICAL', label: 'Critical' },
      ],
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'select',
      width: 6,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
    {
      name: 'triggerRoute',
      label: 'Trigger Route',
      type: 'select',
      width: 6,
      required: true,
      options: ENABLE_ALL_TRIGGER_ROUTES ? [
        { value: 'API', label: 'API' },
        { value: 'LLM', label: 'LLM' },
        { value: 'Agent', label: 'Agent' },
        { value: 'Event', label: 'Event' },
        { value: 'MCP', label: 'MCP' },
      ] : [
        { value: 'API', label: 'API', disabled: true },
        { value: 'LLM', label: 'LLM' },
        { value: 'Agent', label: 'Agent', disabled: true },
        { value: 'Event', label: 'Event', disabled: true },
        { value: 'MCP', label: 'MCP', disabled: true },
      ],
      helperText: ENABLE_ALL_TRIGGER_ROUTES
        ? 'How this task is triggered: API, LLM (LangChain agent), Agent, Event, or MCP. Required to avoid task failures in the queue.'
        : undefined,
      validate: (value) => (!value || !String(value).trim() ? 'Trigger Route is required' : null),
    },
    {
      name: 'questions',
      label: 'Questions / Prompt',
      required: true,
      type: 'text',
      width: 12,
      multiline: true,
      rows: 3,
      helperText: 'Describe what this task should do when triggered.',
      validate: (value) => (!value || !String(value).trim() ? 'Questions / Prompt is required' : null),
    },
    {
      name: 'runActions',
      label: 'Run Actions (JSON)',
      type: 'text',
      width: 12,
      multiline: true,
      rows: 6,
      helperText: 'Full JSON for run_actions. Trigger route and questions above are merged in on save. You can also edit JSON directly.',
      validate: (value) => {
        if (value) {
          try {
            JSON.parse(value);
            return null;
          } catch (e) {
            return 'Invalid JSON format';
          }
        }
        return null;
      },
    },
    {
      name: 'payloadTemplate',
      label: 'Payload Template (JSON)',
      type: 'text',
      width: 12,
      multiline: true,
      rows: 4,
      helperText: 'Optional JSON template for task payload',
      validate: (value) => {
        if (value) {
          try {
            JSON.parse(value);
            return null;
          } catch (e) {
            return 'Invalid JSON format';
          }
        }
        return null;
      },
    },
  ], [drawerMode, propAgentName, propAgentDisplayName, tenantAgents]);

  const initialFormData = {
    agentName: propAgentName || '', // Pre-fill if provided
    taskName: '',
    taskType: '',
    runType: '',
    runTime: '',
    recurrence: 'DAILY',
    selectedWeekdays: [1],
    dayOfMonth: 1,
    selectedMonths: [1],
    scheduleStartDate: '',
    scheduleEndDate: '',
    runIntervalMin: '',
    activeFrom: '06:00',
    activeTo: '22:00',
    activeWindowPreset: 'custom',
    isActive: true,
    priority: 50,
    notifyOnSeverity: 'HIGH',
    triggerRoute: '',
    questions: '',
    runActions: '',
    payloadTemplate: '',
    staticTaskId: '',
  };

  const breadcrumbPaths = drawerMode ? [] : generateBreadcrumbPath('Agent Static Tasks');

  return (
    <>
      <DynamicContainer
        title={drawerMode ? '' : "Agent Static Tasks"}
        icon={Task}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="staticTaskId"
        breadcrumbPaths={breadcrumbPaths}
        rows={staticTasks}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        prepareEditData={prepareEditData}
        isLoading={isLoading}
      />
      {/* Deactivate confirmation dialog */}
      <Dialog
        open={deactivateConfirmOpen}
        onClose={closeDeactivateConfirm}
        aria-labelledby="deactivate-dialog-title"
        aria-describedby="deactivate-dialog-description"
      >
        <DialogTitle id="deactivate-dialog-title">Confirm Deactivate Task</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <AlertTitle>Warning</AlertTitle>
            Are you sure you want to deactivate this task?
            {deactivateTask?.taskName && (
              <strong> "{deactivateTask.taskName}"</strong>
            )}
            {' '}The task will stop running until you activate it again.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivateConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeactivate} color="warning" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentStaticTasks;
